import React, { useEffect } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Line } from 'components';
import {
  getApplicationDisplayDescription,
  getApplicationDisplayName,
  getApplicationIconUrl,
  getApplicationInitials,
  isOrgReadOnly,
  useSpot
} from 'framework';
import { OrgApplication } from 'domain-types';

const Description = styled(Typography)`
  color: ${p => p.theme.palette.text.secondary};
`;

const EmptyState = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const ApplicationsGrid = styled(Box)`
  display: grid;
  gap: ${p => p.theme.spacing(2)}px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const ApplicationCard = styled(Box)`
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: ${p => p.theme.spacing(1)}px;
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(2)}px;
  justify-content: space-between;
  min-height: 180px;
  padding: ${p => p.theme.spacing(2)}px;
`;

const ApplicationHeader = styled(Box)`
  align-items: center;
  display: flex;
  gap: ${p => p.theme.spacing(2)}px;
`;

const ApplicationText = styled(Box)`
  min-width: 0;
`;

const ApplicationMeta = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
`;

const AppIconFallback = styled(Box)`
  align-items: center;
  background: ${p => p.theme.palette.grey[200]};
  border-radius: 12px;
  display: flex;
  font-weight: 600;
  height: 48px;
  justify-content: center;
  text-transform: uppercase;
  width: 48px;
`;

const AppIconImage = styled.img`
  border-radius: 12px;
  height: 48px;
  object-fit: contain;
  width: 48px;
`;

function ApplicationIcon({ application }: { application: OrgApplication }) {
  const [imgError, setImgError] = React.useState(false);
  const iconUrl = getApplicationIconUrl(application);

  if (!iconUrl || imgError) {
    return (
      <AppIconFallback>{getApplicationInitials(application)}</AppIconFallback>
    );
  }

  return (
    <AppIconImage alt="" src={iconUrl} onError={() => setImgError(true)} />
  );
}

const dedupeIds = (values: string[]) =>
  values.filter(
    (value, index) => value.trim().length > 0 && values.indexOf(value) === index
  );

export function Applications({
  id,
  organization
}: {
  id: string;
  organization: string;
}) {
  const { t } = useTranslation();
  const { data, command, query } = useSpot();

  const user = data?.users?.[id];
  const org = data?.organizations?.[organization];
  const readOnly = isOrgReadOnly(org);
  const applications = org?.applications ?? [];
  const shouldLookupOrg = !data?.organizations?.[organization];

  useEffect(() => {
    (async () => {
      if (shouldLookupOrg) {
        await query(`org/${organization}`, {}, ['organizations', organization]);
      }
      await query(`org/${organization}/user/${id}`, {}, ['users', id]);
    })();
  }, [id, organization, query, shouldLookupOrg]);

  const updateApplicationIds = async (
    applicationId: string,
    enabled: boolean
  ) => {
    const currentApplicationIds = user?.applicationIds ?? [];
    const nextApplicationIds = enabled
      ? [...currentApplicationIds, applicationId]
      : currentApplicationIds.filter(
          currentApplicationId => currentApplicationId !== applicationId
        );

    await command(`org/${organization}/user/${id}`, {
      applicationIds: dedupeIds(nextApplicationIds)
    });
    await query(`org/${organization}/user/${id}`, {}, ['users', id]);
  };

  return (
    <>
      <Description variant="body2">{t('applicationsDescription')}</Description>
      <Line />
      {applications.length ? null : (
        <EmptyState>
          <Alert severity="info">{t('applicationNoOrgApps')}</Alert>
        </EmptyState>
      )}
      <ApplicationsGrid>
        {applications.map(application => {
          const isInherited = application.visibility === 'all_users';
          const isEnabled = isInherited
            ? true
            : (user?.applicationIds ?? []).includes(application.id);

          return (
            <ApplicationCard key={application.id}>
              <Box>
                <ApplicationHeader>
                  <ApplicationIcon application={application} />
                  <ApplicationText>
                    <Typography variant="subtitle1">
                      {getApplicationDisplayName(application)}
                    </Typography>
                    {getApplicationDisplayDescription(application) ? (
                      <Description variant="body2">
                        {getApplicationDisplayDescription(application)}
                      </Description>
                    ) : null}
                  </ApplicationText>
                </ApplicationHeader>
              </Box>
              <ApplicationMeta>
                <Description variant="caption">
                  {isInherited
                    ? t('applicationInheritedAccessDescription')
                    : isEnabled
                    ? t('applicationAccessGranted')
                    : t('applicationAccessNotGranted')}
                </Description>
              </ApplicationMeta>
              <Box>
                <Button
                  color="primary"
                  disabled={readOnly || isInherited}
                  fullWidth
                  variant={isEnabled ? 'contained' : 'outlined'}
                  onClick={() =>
                    updateApplicationIds(application.id, !isEnabled)
                  }
                >
                  {isInherited
                    ? t('applicationAccessAllUsers')
                    : isEnabled
                    ? t('applicationRemoveAccess')
                    : t('applicationGrantAccess')}
                </Button>
              </Box>
            </ApplicationCard>
          );
        })}
      </ApplicationsGrid>
    </>
  );
}
